import React, { useCallback, useEffect, useState } from 'react';
import { SpriteIconsLight } from 'src/config/api/models/FontAwesome';
import TrajectoryToolboxButton from './TrajectoryToolboxButton';
import { useAppDispatch, useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import { useUser } from 'src/modules/shared';
import { TrajectoryToolboxMenu } from './Atoms';
import { DownloadDialog } from 'src/modules/notes/components/DownloadDialog';
import { useParams } from 'react-router';
import { UrlParams } from 'src/modules/shared/types';
import { LogActionTypes, useDataLogging } from 'src/modules/shared/data-logging';
import {
  SidePanelModes,
  changePanelContent,
  openSidePanel,
} from 'src/modules/shared/slices/sidePanel/sidePanelSlice';
import {
  getBlockNotes,
  useDownloadNotesPerPageMutation,
  useDownloadNotesPerTrajectoryMutation,
  useGetNotesByBookQuery,
} from 'src/modules/shared/slices/notes/api/notes';
import {
  setCurrentStudent,
  setStudentMode,
} from 'src/modules/shared/slices/notes/entity/BlockNotesSlice';

interface TrajectoryToolboxProps {
  bookId: string;
  pageId: string;
}

export const TrajectoryToolboxNotesPanel: React.FC<TrajectoryToolboxProps> = ({
  bookId,
  pageId,
}) => {
  const dispatch = useAppDispatch();
  const sidePanelMode = useAppSelector((state) => state.sidepanel.sidePanelMode);
  const studentMode = useAppSelector((state) => state.blockNotes.studentMode);

  const { isTeacher } = useUser();
  const [openDialog, setOpenDialog] = React.useState(false);
  const { trajectoryId, chapterId, sectionName }: UrlParams = useParams();
  const log = useDataLogging();

  const { refetch: refetchBlockNotes } = getBlockNotes({ pageId });
  const { refetch: refetchGroupNotes } = useGetNotesByBookQuery({
    pageId,
    bookId,
  }, { skip: !isTeacher});

  const [downloadPerPage] = useDownloadNotesPerPageMutation();
  const [downloadPerTrajectory] = useDownloadNotesPerTrajectoryMutation();

  const handleSwitchNotesView = useCallback(() => {
    if (!studentMode) {
      dispatch(setStudentMode(true));
      dispatch(changePanelContent(SidePanelModes.GROUP_NOTES));
      log(LogActionTypes.SHOW_STUDENTNOTES, {
        trajectoryId,
        chapterId,
        bookId,
      });
      refetchGroupNotes();
    } else {
      dispatch(changePanelContent(SidePanelModes.NOTES));
      refetchBlockNotes();
      dispatch(setStudentMode(false));
      dispatch(setCurrentStudent(undefined));
    }
  }, [
    bookId,
    chapterId,
    trajectoryId,
    log,
    refetchBlockNotes,
    refetchGroupNotes,
    studentMode,
    dispatch,
  ]);

  const [menuOpen, setMenuOpen] = useState(false);

  const toggleNotesMode = useCallback(
    () => {
      setMenuOpen(!menuOpen);
    },
    [menuOpen]
  );

  useEffect(() => {
    return () => {
      setMenuOpen(false);
      dispatch(changePanelContent(SidePanelModes.NONE));
      dispatch(openSidePanel(false));
      dispatch(setStudentMode(false));
      dispatch(setCurrentStudent(undefined));
    };
    // eslint-disable-next-line
  }, [dispatch, menuOpen]);

  return (
    <TrajectoryToolboxMenu>
      <TrajectoryToolboxButton
        id={'trajectory-toolbox-notes'}
        tooltip={'Notities'}
        icon={SpriteIconsLight.NOTEBOOK}
        onClick={toggleNotesMode}
        active={sidePanelMode === SidePanelModes.NOTES}
      />
      <TrajectoryToolboxButton
        tooltip={'Notities downloaden'}
        icon={SpriteIconsLight.CLOUD_ARROW_DOWN}
        onClick={() => setOpenDialog(true)}
      />
      {isTeacher && (
        <TrajectoryToolboxButton
          tooltip={studentMode ? 'Mijn notities bekijken' : 'Notities van leerlingen bekijken'}
          icon={studentMode ? SpriteIconsLight.NOTEBOOK : SpriteIconsLight.CIRCLE_USER}
          onClick={handleSwitchNotesView}
        />
      )}

      <DownloadDialog
        onClickCancel={() => setOpenDialog(false)}
        open={openDialog}
        trajectoryId={trajectoryId as string}
        page={{ pageId, pageName: sectionName }}
        onDownload={(value: string) => {
          if (value === pageId) {
            downloadPerPage({ bookId: bookId, pageId });
            // we want to register the user interation, we dont need to wait for
            // axios response
            log(LogActionTypes.PRINT_NOTES, {
              trajectoryId,
              chapterId,
              bookId,
              pageId: pageId,
            });
            return setOpenDialog(false);
          }
          downloadPerTrajectory({ bookId: bookId, trajectoryId: value });
          log(LogActionTypes.PRINT_NOTES, {
            trajectoryId,
            chapterId,
            bookId,
          });
          setOpenDialog(false);
        }}
      />
    </TrajectoryToolboxMenu>
  );
};
